import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';

import { ApiStatusLine } from '@/features/system';
import { BrandMark } from '@/shared/ui/brand-mark';
import { LegalLinks } from '@/shared/ui/legal-links';

import { SignInCard } from '../components/sign-in-card';

export interface LandingPageProps {
  redirectTo?: string | undefined;
}

const PILLARS = ['provision', 'health', 'audit'] as const;

/**
 * The portal for signed-out visitors: statement on the left, sign-in on the right. Always
 * dark regardless of the console theme (the `dark` class scopes the dark tokens to this tree).
 */
export function LandingPage({ redirectTo }: LandingPageProps) {
  const { t } = useTranslation('auth');
  const { apiBaseUrl } = getRuntimeConfig();

  return (
    <div className="dark bg-background text-foreground min-h-svh lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
      <section className="bg-card/40 flex flex-col justify-between gap-12 border-b p-6 sm:p-10 lg:border-r lg:border-b-0 lg:p-14">
        <div className="flex items-center gap-3">
          <BrandMark />
          <span className="text-lg font-semibold tracking-tight">{t('landing.brand')}</span>
          <span className="text-muted-foreground border-l pl-3 text-[13px]">
            {t('landing.surface')}
          </span>
        </div>

        <div className="flex max-w-xl flex-col gap-7">
          <h1 className="font-serif text-4xl leading-[1.05] font-normal tracking-tight sm:text-5xl lg:text-[56px]">
            {t('landing.headline')}
          </h1>
          <p className="text-muted-foreground max-w-lg text-base leading-relaxed sm:text-lg">
            {t('landing.statement')}
          </p>
          <dl className="grid gap-6 pt-1 sm:grid-cols-3">
            {PILLARS.map((pillar) => (
              <div key={pillar} className="flex flex-col gap-1">
                <dt className="text-[13px] font-semibold">
                  {t(`landing.pillars.${pillar}.title`)}
                </dt>
                <dd className="text-muted-foreground text-[13px] leading-snug">
                  {t(`landing.pillars.${pillar}.body`)}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <footer className="flex flex-col gap-3 text-[13px] sm:flex-row sm:items-end sm:justify-between">
          <ApiStatusLine />
          <nav aria-label={t('landing.footer.links')} className="flex gap-5">
            <a
              href={`${apiBaseUrl}/docs`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              {t('landing.footer.docs')}
            </a>
            <a
              href={`${apiBaseUrl}/ready`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:underline"
            >
              {t('landing.footer.readiness')}
            </a>
          </nav>
          <LegalLinks className="text-muted-foreground [&_a:hover]:text-foreground [&_a:hover]:underline" />
        </footer>
      </section>

      <section className="flex items-center justify-center p-6 sm:p-10">
        <SignInCard redirectTo={redirectTo} />
      </section>
    </div>
  );
}
